// Eingabe eines Bestätigungscodes: sechs Ziffern aus der Authenticator-App (große Ziffern, Ziffernfeld,
// `autocomplete="one-time-code"` – iOS schlägt Codes aus „Passwörter“ vor) oder ein Wiederherstellungscode
// (ABCD-EFGH-JKMN, Groß/klein egal). Ein eingefügter ganzer Code („123 456“, „Code: 123456“) wird übernommen.
// docs/anmeldung/SPEZIFIKATION.md §6.5, §6.6, §6.10, §10.1. Benutzt von Anmeldung, Einrichtung, Profil (AP-U).
import { RECOVERY_CODES, TOTP, formatRecoveryCode, normalizeRecoveryCode } from '@shared/auth'
import { useRef, type Ref } from 'react'
import { cn } from '../../lib/cn'
import { controlClass, FieldFrame, useFieldIds } from '../../ui/Field'

export type CodeKind = 'totp' | 'recovery'

export interface CodeInputProps {
  value: string
  onChange: (value: string) => void
  /** 'totp': sechs Ziffern (Standard) · 'recovery': Wiederherstellungscode */
  kind?: CodeKind
  /** der Code ist vollständig (eingetippt oder eingefügt) – z. B. gleich absenden */
  onComplete?: (code: string) => void
  /** Standard: „Code aus Ihrer App“ bzw. „Wiederherstellungscode“ */
  label?: string
  labelHidden?: boolean
  hint?: string
  error?: string
  autoFocus?: boolean
  disabled?: boolean
  id?: string
  name?: string
  className?: string
  ref?: Ref<HTMLInputElement>
  'data-testid'?: string
}

/** Eingabe auf das Erlaubte kürzen: Ziffern bzw. Zeichen des Codes-Alphabets, mit Bindestrichen gegliedert. */
export function sanitizeCode(kind: CodeKind, raw: string): string {
  if (kind === 'totp') return raw.replace(/\D/g, '').slice(0, TOTP.digits)
  const chars = raw
    .toUpperCase()
    .replace(/O/g, '0')
    .replace(/[IL]/g, '1')
    .replace(/[^0-9A-Z]/g, '')
    .split('')
    .filter((ch) => RECOVERY_CODES.alphabet.includes(ch))
    .join('')
    .slice(0, RECOVERY_CODES.length)
  return formatRecoveryCode(chars)
}

/** vollständig eingegeben? (6 Ziffern bzw. 12 gültige Zeichen) */
export const isCompleteCode = (kind: CodeKind, value: string): boolean =>
  kind === 'totp' ? new RegExp(`^\\d{${TOTP.digits}}$`).test(value) : normalizeRecoveryCode(value) !== null

export function CodeInput({
  value,
  onChange,
  kind = 'totp',
  onComplete,
  label,
  labelHidden,
  hint,
  error,
  autoFocus,
  disabled,
  id,
  name,
  className,
  ref,
  'data-testid': testId,
}: CodeInputProps) {
  const ids = useFieldIds({ id, hint, error })
  const inputRef = useRef<HTMLInputElement | null>(null)
  const totp = kind === 'totp'

  const change = (raw: string): void => {
    const next = sanitizeCode(kind, raw)
    onChange(next)
    if (onComplete && isCompleteCode(kind, next) && !isCompleteCode(kind, value)) onComplete(next)
  }

  return (
    <FieldFrame
      ids={ids}
      label={label ?? (totp ? 'Code aus Ihrer App' : 'Wiederherstellungscode')}
      labelHidden={labelHidden}
      hint={hint}
      error={error}
      className={className}
    >
      <div
        className={cn(controlClass({ invalid: ids.invalid, disabled, size: 'lg' }), 'min-h-14')}
        data-ui="field"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) {
            event.preventDefault()
            inputRef.current?.focus()
          }
        }}
      >
        <input
          ref={(node) => {
            inputRef.current = node
            if (typeof ref === 'function') ref(node)
            else if (ref) ref.current = node
          }}
          id={ids.id}
          name={name}
          type="text"
          value={value}
          disabled={disabled}
          autoFocus={autoFocus}
          inputMode={totp ? 'numeric' : 'text'}
          pattern={totp ? '[0-9]*' : undefined}
          autoComplete={totp ? 'one-time-code' : 'off'}
          autoCapitalize={totp ? 'off' : 'characters'}
          autoCorrect="off"
          spellCheck={false}
          // eine Ziffer bzw. ein Zeichen mehr als nötig: ein eingefügtes „123 456“ kommt vollständig an
          maxLength={totp ? 16 : 32}
          aria-invalid={ids.invalid || undefined}
          aria-describedby={ids.describedBy}
          data-testid={testId}
          className={cn(
            'h-full w-full min-w-0 bg-transparent px-3 text-center font-mono text-fg tabular-nums outline-none placeholder:text-fg-subtle disabled:cursor-not-allowed',
            totp ? 'text-display tracking-[0.35em]' : 'text-title tracking-[0.12em] uppercase',
          )}
          onChange={(event) => change(event.target.value)}
          onPaste={(event) => {
            // eingefügter Text mit Beiwerk („Ihr Code: 123 456“) – nur den Code übernehmen
            const text = event.clipboardData.getData('text')
            if (!text) return
            event.preventDefault()
            change(text)
          }}
        />
      </div>
    </FieldFrame>
  )
}
